import React, { useState } from "react";
import { api } from "@/lib/caesar";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Eye, EyeOff, ShieldCheck } from "lucide-react";

function fmtErr(d) {
  if (d == null) return "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return d?.msg || String(d);
}

export default function Login() {
  const { setUser } = useAuth();
  const [mode, setMode] = useState("login"); // login | forgot | 2fa | register
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [resetLink, setResetLink] = useState("");
  const [challenge, setChallenge] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState("");
  const [notice, setNotice] = useState("");

  const googleLogin = () => {
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/")}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      if (mode === "register") {
        const { data } = await api.post("/auth/register", { name, email, password });
        setNotice(data.message || "Your account is pending Super Admin approval.");
        setMode("login"); setPassword(""); setName("");
        toast.success("Account created — pending approval");
      } else if (mode === "forgot") {
        const { data } = await api.post("/auth/forgot-password", { email });
        if (data.reset_token) {
          setResetLink(`${window.location.origin}/reset?token=${data.reset_token}`);
          toast.success("Reset link generated");
        } else {
          toast.success("If the email is authorized, a reset link was generated.");
        }
      } else if (mode === "2fa") {
        const { data } = await api.post("/auth/verify-2fa", { challenge, code });
        setUser(data);
        window.location.href = "/";
      } else {
        const { data } = await api.post("/auth/login", { email, password, remember });
        if (data.two_factor_required) {
          setChallenge(data.challenge);
          setDevCode(data.dev_code || "");
          setMode("2fa");
        } else {
          setUser(data);
          window.location.href = "/";
        }
      }
    } catch (err) {
      setError(fmtErr(err.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full h-12 rounded-lg bg-surface border border-edge px-4 outline-none focus:border-gold/50";

  return (
    <div className="min-h-screen bg-ink text-[#FAFAFA] relative overflow-hidden flex items-center justify-center px-6">
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 50% 25%, rgba(212,175,55,0.10), transparent 55%)" }} />
      <div className="relative w-full max-w-sm animate-fade-up">
        <img src="/szr-logo.png" alt="SZR Command Center" className="mx-auto w-40 h-40 object-contain" />
        <p className="label-caps text-center text-gold -mt-2">Command · Intelligence · Execution</p>
        <p className="text-center text-xs text-[#A3A3A3] mt-1">Private Member Portal — Authorized Access Only</p>

        {mode === "2fa" ? (
          <form onSubmit={submit} className="mt-8 space-y-3" data-testid="twofa-form">
            <div className="flex items-center gap-2 text-sm text-[#A3A3A3]"><ShieldCheck size={16} className="text-gold" /> Enter the 6-digit verification code.</div>
            {devCode && (
              <div data-testid="twofa-devcode" className="text-xs bg-surface border border-gold/30 rounded-lg p-3">
                Email delivery is unavailable in preview. Your code: <span className="font-num text-gold text-base tracking-widest">{devCode}</span>
              </div>
            )}
            <input data-testid="twofa-code" inputMode="numeric" required value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" className={inputCls + " tracking-widest text-center font-num"} />
            {error && <p data-testid="login-error" className="text-sred text-sm">{error}</p>}
            <button data-testid="twofa-submit" disabled={busy} type="submit" className="w-full h-12 rounded-lg bg-gold text-ink font-bold hover:bg-[#e6c14e] transition-colors disabled:opacity-60">{busy ? "…" : "VERIFY"}</button>
            <button type="button" onClick={() => { setMode("login"); setCode(""); setError(""); }} className="w-full text-xs text-[#A3A3A3] hover:text-gold">Back to login</button>
          </form>
        ) : (
          <>
            <form onSubmit={submit} className="mt-8 space-y-3">
              {notice && (
                <div data-testid="signup-notice" className="text-sm bg-surface border border-sgreen/40 text-sgreen rounded-lg p-3">{notice}</div>
              )}
              {mode === "register" && (
                <input data-testid="register-name" type="text" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" className={inputCls} />
              )}
              <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className={inputCls} />
              {mode !== "forgot" && (
                <div className="relative">
                  <input data-testid="login-password" type={showPw ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} placeholder={mode === "register" ? "Create a password (min 8 chars)" : "Password"} className={inputCls + " pr-12"} />
                  <button type="button" data-testid="toggle-password" onClick={() => setShowPw((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A3A3A3] hover:text-gold">
                    {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              )}
              {mode === "login" && (
                <label data-testid="remember-me" className="flex items-center gap-2 text-sm text-[#A3A3A3] cursor-pointer select-none">
                  <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="accent-[#D4AF37] w-4 h-4" /> Remember me
                </label>
              )}
              {error && <p data-testid="login-error" className="text-sred text-sm">{error}</p>}
              {resetLink && (
                <div data-testid="reset-link-box" className="text-xs bg-surface border border-gold/30 rounded-lg p-3 break-all">
                  Reset link (open to set a new password): <a href={resetLink} className="text-gold underline">{resetLink}</a>
                </div>
              )}
              <button data-testid="login-submit" disabled={busy} type="submit" className="w-full h-12 rounded-lg bg-gold text-ink font-bold hover:bg-[#e6c14e] transition-colors disabled:opacity-60">
                {busy ? "…" : mode === "forgot" ? "SEND RESET LINK" : mode === "register" ? "CREATE ACCOUNT" : "SIGN IN"}
              </button>
            </form>

            <div className="flex justify-between mt-3 text-xs">
              {mode !== "register" && (
                <button data-testid="forgot-toggle" onClick={() => { setMode(mode === "forgot" ? "login" : "forgot"); setError(""); setResetLink(""); setNotice(""); }} className="text-[#A3A3A3] hover:text-gold">
                  {mode === "forgot" ? "Back to login" : "Forgot password?"}
                </button>
              )}
              <button data-testid="register-toggle" onClick={() => { setMode(mode === "register" ? "login" : "register"); setError(""); setResetLink(""); setNotice(""); }} className="text-[#A3A3A3] hover:text-gold ml-auto">
                {mode === "register" ? "Back to login" : "Create account"}
              </button>
            </div>

            {mode !== "register" && (
              <>
                <div className="flex items-center gap-3 my-5"><div className="flex-1 h-px bg-edge" /><span className="label-caps text-[0.6rem]">or</span><div className="flex-1 h-px bg-edge" /></div>
                <button data-testid="google-login-btn" onClick={googleLogin} className="w-full h-12 rounded-lg bg-surface border border-edge font-semibold hover:border-gold/40 transition-colors flex items-center justify-center gap-3">
                  <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#fff" d="M21.35 11.1H12v2.9h5.35c-.25 1.6-1.9 4.7-5.35 4.7-3.2 0-5.8-2.65-5.8-5.9s2.6-5.9 5.8-5.9c1.85 0 3.05.8 3.75 1.45l2.55-2.45C16.5 3.9 14.5 3 12 3 6.95 3 2.9 7.05 2.9 12S6.95 21 12 21c5.2 0 8.65-3.65 8.65-8.8 0-.6-.05-1-.15-1.5z" /></svg>
                  Sign in with Google
                </button>
              </>
            )}
          </>
        )}
        <p className="text-xs text-[#5a5a5a] mt-5 text-center">Invitation-only. Access restricted to authorized SZR personnel.</p>
      </div>
    </div>
  );
}
